/**
 * Dataverse OData Utilities
 *
 * Helper functions for building OData queries and handling responses.
 */

import type { QueryOptions, ExpandOption } from './types';

/**
 * Build OData query string from options
 */
export function buildQueryString(options: QueryOptions): string {
  const params: string[] = [];

  // $select
  if (options.select?.length) {
    params.push(`$select=${options.select.join(',')}`);
  }

  // $expand (supports nested options)
  if (options.expand?.length) {
    const expandStr = options.expand
      .map((exp) => {
        if (typeof exp === 'string') {
          return exp;
        }
        return buildExpandOption(exp);
      })
      .join(',');
    params.push(`$expand=${expandStr}`);
  }

  // $filter
  if (options.filter) {
    params.push(`$filter=${encodeURIComponent(options.filter)}`);
  }

  // $orderby
  if (options.orderBy) {
    params.push(`$orderby=${encodeURIComponent(options.orderBy)}`);
  }

  // $top
  if (options.top !== undefined) {
    params.push(`$top=${options.top}`);
  }

  // $skip
  if (options.skip !== undefined) {
    params.push(`$skip=${options.skip}`);
  }

  // $count
  if (options.count) {
    params.push('$count=true');
  }

  // $apply (aggregation)
  if (options.apply) {
    params.push(`$apply=${encodeURIComponent(options.apply)}`);
  }

  return params.length > 0 ? `?${params.join('&')}` : '';
}

/**
 * Build nested expand option string
 */
function buildExpandOption(option: ExpandOption): string {
  const nested: string[] = [];

  if (option.select?.length) {
    nested.push(`$select=${option.select.join(',')}`);
  }

  if (option.filter) {
    nested.push(`$filter=${encodeURIComponent(option.filter)}`);
  }

  if (option.orderBy) {
    nested.push(`$orderby=${encodeURIComponent(option.orderBy)}`);
  }

  if (option.top !== undefined) {
    nested.push(`$top=${option.top}`);
  }

  if (nested.length > 0) {
    return `${option.property}(${nested.join(';')})`;
  }

  return option.property;
}

/**
 * Parse JSON fields in entity
 * Dataverse stores JSON as strings, this parses them to objects
 */
export function parseJsonFields<T extends Record<string, unknown>>(
  entity: T,
  fields: (keyof T)[]
): T {
  const result = { ...entity };

  for (const field of fields) {
    const value = result[field];
    if (typeof value === 'string' && value.trim()) {
      try {
        (result[field] as unknown) = JSON.parse(value);
      } catch {
        // Keep as string if invalid JSON
        console.warn(`Failed to parse JSON field: ${String(field)}`);
      }
    }
  }

  return result;
}

/**
 * Stringify JSON fields for Dataverse
 */
export function stringifyJsonFields<T extends Record<string, unknown>>(
  entity: T,
  fields: (keyof T)[]
): T {
  const result = { ...entity };

  for (const field of fields) {
    const value = result[field];
    if (value !== null && typeof value === 'object') {
      (result[field] as unknown) = JSON.stringify(value);
    }
  }

  return result;
}

/**
 * Prepend statecode eq 0 (Active) to an existing OData filter.
 * Used to exclude soft-deleted (Inactive) records from list queries.
 */
export function activeOnly(existingFilter?: string): string {
  const active = 'statecode eq 0';
  return existingFilter ? `${active} and (${existingFilter})` : active;
}

/**
 * Format lookup field for Dataverse bind syntax
 * @param tableName - The plural table name (e.g., 'vrc_solutions')
 * @param id - The GUID of the related record
 * @returns OData bind reference (e.g., '/vrc_solutions(guid)')
 */
export function formatLookupBind(tableName: string, id: string): string {
  return `/${tableName}(${id})`;
}

/**
 * Extract ID from OData entity URL
 * @param url - Full OData URL (e.g., 'https://org.crm/api/vrc_solutions(guid)')
 * @returns The extracted GUID
 */
export function extractIdFromUrl(url: string): string {
  const match = /\(([0-9a-f-]+)\)$/i.exec(url);
  if (!match?.[1]) {
    throw new Error(`Could not extract ID from URL: ${url}`);
  }
  return match[1];
}

/**
 * Escape a string value for safe inclusion in an OData filter literal.
 * OData escapes single quotes by doubling them. This is necessary but not
 * sufficient on its own — also validate the field's expected shape (e.g.
 * use `assertGuid` for IDs) and reject suspicious operators where possible.
 *
 * Usage: `filter: \`contains(name, '${escapeODataString(userInput)}')\``
 */
export function escapeODataString(value: string): string {
  return value.replace(/'/g, "''");
}

/**
 * Defense-in-depth sanitizer for free-text user-search inputs that hit
 * `contains(fullname, ...)` / `contains(internalemailaddress, ...)`. Beyond
 * single-quote escaping, restricts to characters that legitimately appear
 * in human names + email addresses, caps length, and trims. Anything else
 * is stripped before interpolation, blocking exotic OData/URL edge cases
 * (control chars, unicode, %, &, etc.) from reaching Dataverse.
 */
const SAFE_USER_QUERY_RE = /[^A-Za-z0-9 .@'_\-]/g;
const MAX_USER_QUERY_LEN = 100;

export function sanitizeUserSearchQuery(value: string): string {
  return value.replace(SAFE_USER_QUERY_RE, '').slice(0, MAX_USER_QUERY_LEN).trim();
}

/**
 * Build OData filter for multiple IDs.
 * Each id is validated as a GUID before interpolation to prevent injection.
 * Throws if any id is not a valid GUID.
 */
export function buildIdFilter(fieldName: string, ids: string[]): string {
  if (ids.length === 0) return '';
  for (const id of ids) {
    if (!isValidGuid(id)) {
      throw new Error(`buildIdFilter: invalid GUID for ${fieldName}: ${id}`);
    }
  }
  if (ids.length === 1) return `${fieldName} eq ${ids[0]}`;

  const conditions = ids.map((id) => `${fieldName} eq ${id}`);
  return conditions.join(' or ');
}

/**
 * Format date for Dataverse
 */
export function formatDateForDataverse(date: Date): string {
  return date.toISOString();
}

/**
 * Parse Dataverse date string
 */
export function parseDateFromDataverse(dateStr: string): Date {
  return new Date(dateStr);
}

/**
 * Generate a new GUID (for client-side ID generation)
 */
export function generateGuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Check if a value is a valid GUID
 */
export function isValidGuid(value: string): boolean {
  const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return guidRegex.test(value);
}

/**
 * True when a Dataverse error says a $select or $filter named a column the
 * org does not have yet ("Could not find a property named 'vba_x'"). Lets a
 * read that $selects a freshly added column fall back on an org that has not
 * been provisioned, so a deploy that lands before the schema step degrades
 * instead of failing with a 400. Pass `column` to match only that column.
 */
export function isMissingColumnError(err: unknown, column?: string): boolean {
  if (!(err instanceof Error)) return false;
  if (!/Could not find a property named/i.test(err.message)) return false;
  return column ? err.message.includes(`'${column}'`) : true;
}

